import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Database,
  Eye,
  Share2,
  Clock3,
  Cookie,
  Bell,
  FileText,
  ArrowLeft,
  PhoneCall,
  Server,
} from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SwitchIn";
const PRODUCT_NAME = "SwitchIn Jewellery Retail Software";
const WEBSITE_NAME = "SwitchIn";
const SUPPORT_EMAIL = "nextloopinfotech@gmail.com";
const SUPPORT_PHONE = "+91 96996 24618";
const LEGAL_JURISDICTION = "Pune, Maharashtra, India";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
});

const privacySections = [
  {
    id: "intro",
    icon: <FileText className="h-5 w-5" />,
    title: "1. Introduction",
    content: [
      `This Privacy Policy explains how ${COMPANY_NAME} collects, uses, stores, and protects information when you use ${PRODUCT_NAME}, our website, demo request forms, enquiry forms, and related services.`,
      `By accessing or using ${WEBSITE_NAME}, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the service.`,
    ],
  },
  {
    id: "data-we-collect",
    icon: <Database className="h-5 w-5" />,
    title: "2. Information We Collect",
    content: [
      "We may collect business and contact information that you provide directly, such as store name, owner/manager name, mobile number, email address, city, state, number of branches, and any details shared in enquiry or demo request forms.",
      "When you use the software, we may also process operational business data entered by you or your authorised staff, such as billing data, stock/item details, customer records, scheme information, transaction history, branch information, user accounts, and support-related communication.",
      "We may also collect limited technical information such as browser type, device information, app installation status, pages visited, timestamps, IP-related session logs, and similar diagnostic or usage data required for security, troubleshooting, and service improvement.",
    ],
  },
  {
    id: "how-we-use",
    icon: <Eye className="h-5 w-5" />,
    title: "3. How We Use Information",
    content: [
      "We use collected information to provide and operate the software, respond to enquiries, schedule demos, onboard stores, train staff, provide support, maintain account access, and improve product performance.",
      "We may use data to manage billing, user authentication, security monitoring, system administration, customer support follow-up, feature enhancement, communication, and lawful compliance.",
      "We may also use contact details to communicate important updates related to your account, plan, support, service availability, or product changes.",
    ],
  },
  {
    id: "business-data",
    icon: <Server className="h-5 w-5" />,
    title: "4. Business Data Processed Through the Software",
    content: [
      "The software may process business records submitted by you, including customer, billing, stock, scheme, transaction, and staff-related information.",
      "Such data is processed only for enabling your store operations, reporting, support, backups, troubleshooting, and service delivery.",
      "You remain responsible for ensuring that the data entered by your organisation is lawful, accurate, and authorised for use within your business operations.",
    ],
  },
  {
    id: "sharing",
    icon: <Share2 className="h-5 w-5" />,
    title: "5. Sharing of Information",
    content: [
      "We do not sell your personal information or business data to third parties.",
      "We may share information only where reasonably necessary with service providers, hosting providers, infrastructure providers, communication tools, or technical partners who support our operations, subject to appropriate confidentiality and operational safeguards.",
      "We may also disclose information if required by law, legal process, regulatory obligation, fraud prevention needs, security investigation, or protection of our rights, users, or systems.",
    ],
  },
  {
    id: "security",
    icon: <Lock className="h-5 w-5" />,
    title: "6. Data Security",
    content: [
      "We take reasonable administrative, technical, and operational steps to protect information against unauthorised access, misuse, disclosure, alteration, or destruction.",
      "These steps may include access control, account-based permissions, authenticated access, activity monitoring, backups, and infrastructure-level safeguards where applicable.",
      "However, no internet-based system, software platform, or storage environment can be guaranteed to be completely secure, and therefore absolute security cannot be promised.",
    ],
    highlight: true,
  },
  {
    id: "retention",
    icon: <Clock3 className="h-5 w-5" />,
    title: "7. Data Retention",
    content: [
      "We retain information for as long as needed to provide the service, support your account, maintain records, comply with legal obligations, resolve disputes, enforce agreements, and manage backups or operational continuity.",
      "Retention periods may vary depending on the type of information, commercial relationship, legal requirements, and support or service needs.",
    ],
  },
  {
    id: "cookies",
    icon: <Cookie className="h-5 w-5" />,
    title: "8. Cookies and Similar Technologies",
    content: [
      "Our website or software interfaces may use cookies, local storage, session identifiers, or similar technologies to maintain sign-in state, improve performance, remember preferences, and support analytics or product functionality.",
      "You may control certain browser-based cookie settings through your device or browser. However, disabling some cookies or storage features may affect website or application functionality.",
    ],
  },
  {
    id: "third-party",
    icon: <Bell className="h-5 w-5" />,
    title: "9. Third-Party Tools and Integrations",
    content: [
      "Our service may rely on third-party services such as hosting platforms, cloud infrastructure, analytics tools, communication systems, payment partners, or support tools.",
      "Where third-party tools are involved, information may be processed to the extent necessary for those services to function for account setup, communication, support, security, or software delivery.",
      "Use of such third-party services may also be subject to their own privacy terms and policies.",
    ],
  },
  {
    id: "user-rights",
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "10. Your Choices and Rights",
    content: [
      "You may contact us to update business contact details, correct inaccurate account information, or ask questions related to the information associated with your account.",
      "Where reasonably applicable and subject to legal, contractual, and operational limitations, you may request access, correction, or clarification regarding the information you have submitted to us.",
      "Certain requests may depend on account status, legal obligations, business records, backup systems, security requirements, and the nature of the data involved.",
    ],
  },
  {
    id: "children",
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "11. Children’s Privacy",
    content: [
      `${PRODUCT_NAME} is intended for business and commercial use, not for children.`,
      "We do not knowingly collect personal information directly from children for independent consumer use of this service.",
    ],
  },
  {
    id: "changes",
    icon: <FileText className="h-5 w-5" />,
    title: "12. Changes to This Privacy Policy",
    content: [
      "We may update this Privacy Policy from time to time to reflect product changes, service improvements, legal obligations, or operational requirements.",
      "The updated version will apply from the date it is published on the website or otherwise communicated to you.",
      "Your continued use of the service after an update means you accept the revised Privacy Policy.",
    ],
  },
  {
    id: "jurisdiction",
    icon: <FileText className="h-5 w-5" />,
    title: "13. Governing Context",
    content: [
      `This Privacy Policy is intended to be read along with our Terms of Service and shall be interpreted in connection with applicable laws in India and operational context in ${LEGAL_JURISDICTION}.`,
    ],
  },
  {
    id: "contact",
    icon: <PhoneCall className="h-5 w-5" />,
    title: "14. Contact Us",
    content: [
      `If you have questions about this Privacy Policy, your data, support, or account-related privacy matters, please contact us at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4ff] via-[#fdf9f3] to-[#e8f4fc] text-slate-900">
      {/* background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(129,140,248,0.18),transparent),radial-gradient(ellipse_60%_40%_at_100%_100%,rgba(245,158,11,0.12),transparent),radial-gradient(ellipse_40%_30%_at_0%_100%,rgba(236,72,153,0.08),transparent)]" />
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#6366f1]/20 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-[#f97316]/15 blur-3xl" />
      </div>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-6 md:py-12">
        {/* top back */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp(0)}
          className="mb-6"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </motion.div>

        {/* hero */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.05)}
          className="overflow-hidden rounded-[30px] border border-violet-200/70 bg-white/90 p-6 shadow-xl shadow-violet-200/30 backdrop-blur-sm md:p-8"
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-700 ring-1 ring-violet-200/70">
                <ShieldCheck className="h-3.5 w-3.5" />
                Privacy
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                Privacy Policy
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-[15px]">
                This Privacy Policy explains what information we collect, how we
                use it, when we may share it, and how we protect business and
                account-related data used with {PRODUCT_NAME}.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
                  Business data handling
                </span>
                <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-200/70">
                  Security focused
                </span>
                <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
                  Transparent use of data
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600 shadow-sm">
              <p className="font-semibold text-slate-800">Document details</p>
              <p className="mt-2">
                <span className="font-medium text-slate-700">Product:</span>{" "}
                {PRODUCT_NAME}
              </p>
              {/* <p className="mt-1">
                <span className="font-medium text-slate-700">
                  Last updated:
                </span>{" "}
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </p> */}
              <p className="mt-1">
                <span className="font-medium text-slate-700">Region:</span>{" "}
                India
              </p>
            </div>
          </div>
        </motion.section>

        {/* quick summary */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.1)}
          className="mt-6 rounded-[28px] border border-sky-200/80 bg-gradient-to-r from-sky-50 via-white to-violet-50 p-5 shadow-sm"
        >
          <h2 className="text-base font-semibold text-slate-900">
            Quick privacy summary
          </h2>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                What we collect
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-700">
                Contact info, account info, enquiry/demo info, and software
                usage/business data required to provide the service.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                How we use it
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-700">
                To run the software, support your store, improve features,
                secure accounts, and respond to service requests.
              </p>
            </div>

            <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                Selling data
              </p>
              <p className="mt-2 text-sm font-medium leading-6 text-violet-800">
                We do not sell your personal information or business data.
              </p>
            </div>
          </div>
        </motion.section>

        {/* sections */}
        <section className="mt-8 grid gap-4">
          {privacySections.map((section, index) => (
            <motion.article
              key={section.id}
              initial="hidden"
              animate="visible"
              variants={fadeUp(0.05 + index * 0.02)}
              className={`rounded-[26px] border bg-white/95 p-5 shadow-sm backdrop-blur-sm md:p-6 ${
                section.highlight
                  ? "border-sky-200 ring-1 ring-sky-100"
                  : "border-slate-200/80"
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                    section.highlight
                      ? "bg-sky-100 text-sky-700"
                      : "bg-violet-100 text-violet-700"
                  }`}
                >
                  {section.icon}
                </div>

                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {section.title}
                  </h2>

                  <div className="mt-3 space-y-3">
                    {section.content.map((paragraph, idx) => (
                      <p
                        key={idx}
                        className="text-sm leading-7 text-slate-600 md:text-[15px]"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </section>

        {/* bottom */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.2)}
          className="mt-8 rounded-[28px] border border-slate-200/80 bg-white/90 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900">
            Questions about privacy?
          </h3>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            For any questions related to data handling, account information,
            contact details, privacy requests, or support, reach out to us.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black"
            >
              Email support
            </a>

            <a
              href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-violet-300 hover:text-violet-700"
            >
              Call support
            </a>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
